"use client";

import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ChevronDown, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import Image from "next/image";

interface Country {
  name: string;
  code: string;
  flag: string;
  dialCode: string;
  maxLength: number;
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
  id?: string;
  className?: string;
  required?: boolean;
}

const defaultMaxLengths: Record<string, number> = {
  IN: 10,
  US: 10,
  GB: 10,
  AU: 9,
  CA: 10,
  DE: 11,
  FR: 9,
  JP: 10,
  CN: 11,
  BR: 11,
  SG: 8,
  AE: 9,
};

export const PhoneNumberInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error,
  label = "Phone Number",
  id = "phone",
  className,
  required = false,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [open, setOpen] = useState(false);
  const [inputError, setInputError] = useState<string | undefined>(error);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://restcountries.com/v3.1/all?fields=name,cca2,flags,idd"
        );
        const data = await response.json();

        interface RawCountryData {
          name: {
            common: string;
          };
          cca2: string;
          flags?: {
            svg: string;
          };
          idd: {
            root: string;
            suffixes: string[];
          };
        }

        const formattedCountries: Country[] = data
          .filter(
            (country: RawCountryData) =>
              country.idd &&
              country.idd.root &&
              country.idd.suffixes &&
              country.idd.suffixes.length > 0
          )
          .map((country: RawCountryData) => {
            const suffix: string = country.idd.suffixes[0];
            return {
              name: country.name.common,
              code: country.cca2,
              flag:
                country.flags?.svg ||
                `https://flagcdn.com/${country.cca2.toLowerCase()}.svg`,
              dialCode: `${country.idd.root}${suffix}`,
              maxLength:
                defaultMaxLengths[
                  country.cca2 as keyof typeof defaultMaxLengths
                ] || 10,
            };
          })
          .sort((a: Country, b: Country) => a.name.localeCompare(b.name));

        setCountries(formattedCountries);
        const india = formattedCountries.find((c) => c.code === "IN");
        setSelectedCountry(india || formattedCountries[0]);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
        const fallbackCountries: Country[] = [
          {
            name: "India",
            code: "IN",
            flag: "https://flagcdn.com/in.svg",
            dialCode: "+91",
            maxLength: 10,
          },
          {
            name: "United States",
            code: "US",
            flag: "https://flagcdn.com/us.svg",
            dialCode: "+1",
            maxLength: 10,
          },
          {
            name: "United Kingdom",
            code: "GB",
            flag: "https://flagcdn.com/gb.svg",
            dialCode: "+44",
            maxLength: 10,
          },
        ];
        setCountries(fallbackCountries);
        setSelectedCountry(fallbackCountries[0]);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (
      value &&
      selectedCountry &&
      value !== `${selectedCountry.dialCode}${phoneNumber}`
    ) {
      for (const country of countries) {
        if (value.startsWith(country.dialCode)) {
          setSelectedCountry(country);
          const extracted = value.substring(country.dialCode.length);
          setPhoneNumber(extracted);

          if (required && extracted.length === 0) {
            setInputError("Phone number is required");
          } else if (
            extracted.length > 0 &&
            extracted.length < country.maxLength
          ) {
            setInputError(
              `Phone number should be ${country.maxLength} digits for ${country.name}`
            );
          } else {
            setInputError(undefined);
          }
          return;
        }
      }
      const cleaned = value.replace(/\D/g, "");
      setPhoneNumber(cleaned);
    }
  }, [value, countries, selectedCountry, required, phoneNumber]);

  useEffect(() => {
    if (error) {
      setInputError(error);
    }
  }, [error]);

  const formatPhoneNumber = (input: string) => {
    const cleaned = input.replace(/\D/g, "");
    if (selectedCountry) {
      if (cleaned.length > selectedCountry.maxLength) return;

      setPhoneNumber(cleaned);
      const fullNumber = `${selectedCountry.dialCode}${cleaned}`;
      onChange(fullNumber);

      if (required && cleaned.length === 0) {
        setInputError("Phone number is required");
      } else if (
        cleaned.length > 0 &&
        cleaned.length < selectedCountry.maxLength
      ) {
        setInputError(
          `Phone number should be ${selectedCountry.maxLength} digits for ${selectedCountry.name}`
        );
      } else {
        setInputError(undefined);
      }
    }
  };

  const selectCountry = (country: Country) => {
    setSelectedCountry(country);

    const truncated = phoneNumber.substring(0, country.maxLength);
    setPhoneNumber(truncated);
    onChange(`${country.dialCode}${truncated}`);

    if (required && truncated.length === 0) {
      setInputError("Phone number is required");
    } else if (truncated.length > 0 && truncated.length < country.maxLength) {
      setInputError(
        `Phone number should be ${country.maxLength} digits for ${country.name}`
      );
    } else {
      setInputError(undefined);
    }

    setOpen(false);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className='ml-1 text-destructive'>*</span>}
        </Label>
      )}
      <div className='flex'>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={open}
              className='flex w-auto items-center gap-2 rounded-r-none border-r-0'
              type='button'
              disabled={loading}
            >
              {loading ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : selectedCountry ? (
                <>
                  <Image
                    src={selectedCountry.flag}
                    alt={selectedCountry.name}
                    width={24}
                    height={16}
                    className='h-4 w-6 object-contain'
                  />
                  <span className='text-sm font-medium'>
                    {selectedCountry.dialCode}
                  </span>
                  <ChevronDown className='h-4 w-4 opacity-50' />
                </>
              ) : (
                <span>Select</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-[350px] p-0'>
            <Command>
              <CommandInput placeholder='Search country...' />
              <CommandList className='max-h-[300px]'>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((country) => (
                    <CommandItem
                      key={country.code}
                      value={`${country.name} ${country.dialCode}`}
                      onSelect={() => selectCountry(country)}
                      className='flex items-center gap-2'
                    >
                      <Image
                        src={country.flag}
                        alt={country.name}
                        width={24}
                        height={16}
                        className='h-4 w-6 object-contain'
                      />
                      <span className='truncate text-sm'>{country.name}</span>
                      <span className='ml-auto text-sm text-muted-foreground'>
                        {country.dialCode}
                      </span>
                      {selectedCountry?.code === country.code && (
                        <Check className='ml-2 h-4 w-4' />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Input
          id={id}
          type='tel'
          className='flex-1 rounded-l-none'
          placeholder={
            selectedCountry
              ? `Enter ${selectedCountry.maxLength} digit number`
              : "Enter phone number"
          }
          value={phoneNumber}
          onChange={(e) => formatPhoneNumber(e.target.value)}
          disabled={loading || !selectedCountry}
          required={required}
        />
      </div>
      {inputError && <p className='text-sm text-destructive'>{inputError}</p>}
    </div>
  );
};
